import { queryOptions, useQuery } from '@tanstack/react-query';
import { SortDTO } from '../common';
import {
  TruckRecordingMipControllerGetRecordingsData,
  TruckRecordingMipDTO,
  TruckRecordingMipFilterDTO,
  TruckRecordingMipService,
} from '../generated';

export type TruckRecordingMip = Omit<TruckRecordingMipDTO, 'observedAt'> & {
  observedAt: Date;
};

export interface TruckRecordingMipListOptionsProps {
  jobsiteId?: string;
  filter: TruckRecordingMipFilterDTO;
  pageSize: number;
  recordingsPage: number;
  recordingsSorting: SortDTO<TruckRecordingMip>;
}

export const truckRecordingMipListQueryKeys = {
  base: () => ['truckRecordingsMip'] as const,
  list: ({ jobsiteId, filter, pageSize, recordingsPage, recordingsSorting }: TruckRecordingMipListOptionsProps) => [
    ...truckRecordingMipListQueryKeys.base(),
    jobsiteId,
    filter,
    pageSize,
    recordingsPage,
    recordingsSorting,
  ],
};

export function truckRecordingMipListOptions({
  jobsiteId,
  filter,
  pageSize,
  recordingsPage,
  recordingsSorting,
}: TruckRecordingMipListOptionsProps) {
  return queryOptions({
    queryKey: truckRecordingMipListQueryKeys.list({ jobsiteId, filter, pageSize, recordingsPage, recordingsSorting }),
    queryFn: async () => {
      if (!jobsiteId) {
        throw new Error('jobsiteId is required');
      }

      const params: TruckRecordingMipControllerGetRecordingsData = {
        path: { jobsiteId },
        query: {
          filter: [toMipFilter(filter)],
          limit: pageSize,
          offset: recordingsPage * pageSize,
          ...recordingsSorting,
        },
      };

      const response = await TruckRecordingMipService.truckRecordingMipControllerGetRecordings(params);

      if (response.data !== undefined) {
        return { ...response.data, data: response.data.data.map(toTruckRecordingMip) };
      }

      throw (response as any).error || new Error(response.response?.statusText || 'Unknown error');
    },
    enabled: !!jobsiteId,
  });
}

export function useTruckRecordingMipList(props: TruckRecordingMipListOptionsProps) {
  return useQuery(truckRecordingMipListOptions(props));
}

function toTruckRecordingMip(recording: TruckRecordingMipDTO): TruckRecordingMip {
  return {
    ...recording,
    observedAt: new Date(recording.observedAt),
  };
}

function toMipFilter(filter: TruckRecordingMipFilterDTO): TruckRecordingMipFilterDTO {
  const observedAtRange = filter.observedAt?.range;
  if (!observedAtRange) {
    return filter;
  }

  const { lt, ...rangeWithoutLt } = observedAtRange;
  const lte = (observedAtRange as { lte?: string }).lte ?? lt;
  const range = lte ? { ...rangeWithoutLt, lte } : rangeWithoutLt;

  return {
    ...filter,
    observedAt: {
      ...filter.observedAt,
      range: range as typeof observedAtRange,
    },
  };
}
